import React, { useState } from "react";
import { Sun, Thermometer, Fan, Zap } from "lucide-react";
import Seo from "../components/Seo";

/**
 * Formulaire d'étude gratuite séchoir solaire — envoie vers
 * POST /api/emails/etude-sechoir (backend → contact@thermasol-airsystems.fr).
 * Réutilisé par /sechoir-solaire (id="etude").
 */
export function EtudeForm() {
  const [form, setForm] = useState({
    nom: "", societe: "", telephone: "", email: "",
    matiere: "", tonnage: "", surface: "", orientation: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const r = await fetch(`${API}/api/emails/etude-sechoir`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setStatus(r.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  };

  const input = "w-full border border-slate-300 rounded-lg px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500";
  const label = "block text-sm font-semibold text-slate-700 mb-1";

  if (status === "sent") {
    return (
      <div id="etude" data-testid="etude-form-success" className="bg-green-50 border border-green-300 rounded-2xl p-8 text-center">
        <p className="text-xl font-bold text-green-800">Merci ! Votre demande d'étude gratuite a bien été envoyée.</p>
        <p className="text-green-700 mt-2">Notre bureau d'études vous répond sous 48 h ouvrées.</p>
        <p className="mt-4 text-slate-700 font-semibold">01 84 19 04 49 — contact@thermasol-airsystems.fr</p>
      </div>
    );
  }

  return (
    <form id="etude" data-testid="etude-form" onSubmit={submit} className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
      <div><label className={label}>Nom *</label><input data-testid="etude-nom" required className={input} value={form.nom} onChange={set("nom")} /></div>
      <div><label className={label}>Société</label><input data-testid="etude-societe" className={input} value={form.societe} onChange={set("societe")} /></div>
      <div><label className={label}>Téléphone *</label><input data-testid="etude-telephone" required type="tel" className={input} value={form.telephone} onChange={set("telephone")} /></div>
      <div><label className={label}>E-mail *</label><input data-testid="etude-email" required type="email" className={input} value={form.email} onChange={set("email")} /></div>
      <div><label className={label}>Matière à sécher</label><input data-testid="etude-matiere" className={input} placeholder="Bois bûches, plaquettes, foin, céréales…" value={form.matiere} onChange={set("matiere")} /></div>
      <div><label className={label}>Tonnage annuel</label><input data-testid="etude-tonnage" className={input} placeholder="ex : 500 t/an" value={form.tonnage} onChange={set("tonnage")} /></div>
      <div><label className={label}>Surface de toiture (m²)</label><input data-testid="etude-surface" className={input} placeholder="ex : 800 m²" value={form.surface} onChange={set("surface")} /></div>
      <div><label className={label}>Orientation de la toiture</label><input data-testid="etude-orientation" className={input} placeholder="ex : Sud, Sud-Est…" value={form.orientation} onChange={set("orientation")} /></div>
      <div className="md:col-span-2"><label className={label}>Message</label><textarea data-testid="etude-message" rows={4} className={input} value={form.message} onChange={set("message")} /></div>
      {status === "error" && (
        <p className="md:col-span-2 text-red-600 text-sm" data-testid="etude-form-error">Une erreur est survenue. Réessayez ou appelez-nous au 01 84 19 04 49.</p>
      )}
      <div className="md:col-span-2">
        <button data-testid="etude-submit" type="submit" disabled={status === "sending"} className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-full transition-colors">
          {status === "sending" ? "Envoi en cours…" : "Envoyer ma demande"}
        </button>
        <p className="text-slate-600 text-sm mt-4">01 84 19 04 49 — contact@thermasol-airsystems.fr</p>
      </div>
    </form>
  );
}

const FAQ = [
  ["Qu'est-ce qu'un séchoir solaire ?", "Un séchoir solaire utilise l'énergie du soleil pour produire de l'air chaud et sécher du bois ou des produits agricoles, sans énergie fossile."],
  ["Combien de temps faut-il pour sécher du bois avec un séchoir solaire ?", "Selon l'essence et le taux d'humidité initial, un séchoir solaire Thermasol permet d'atteindre moins de 20 % d'humidité en quelques semaines."],
  ["Un séchoir solaire est-il éligible à une aide ?", "Oui, la fiche CEE AGRI-EQ-110 finance les systèmes de séchage solaire neufs pour les exploitations agricoles et forestières."],
  ["Qu'est-ce qu'un panneau PVT air ?", "Un panneau PVT air est un panneau hybride photovoltaïque-thermique qui produit de l'électricité et réchauffe l'air circulant en sous-face grâce à un radiateur aluminium, sans fluide caloporteur. C'est la technologie du séchoir solaire Thermasol Air Systems®."],
  ["Qu'est-ce que la fiche AGRI-EQ-110 ?", "La fiche AGRI-EQ-110 est une fiche d'opération standardisée des Certificats d'Économies d'Énergie qui définit les conditions d'éligibilité et le montant de la prime pour un système de séchage solaire dans le secteur agricole et forestier."],
  ["Quel est le prix d'un séchoir solaire ?", "Le prix varie selon la surface de panneaux. L'étude Thermasol est gratuite et intègre le montant de la prime CEE."],
  ["Un séchoir solaire fonctionne-t-il en hiver ?", "Oui : le système fonctionne dès qu'il y a du rayonnement, avec un rendement réduit en hiver ; le débit d'air est régulé automatiquement."],
];

const DESCRIPTION = "Séchoir solaire thermovoltaïque (technologie déposée INPI) : air chaud gratuit pour sécher bois, fourrage, céréales. Éligible fiche CEE AGRI-EQ-110. Étude gratuite sous 48 h.";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Séchoir solaire Thermasol Air Systems®",
    brand: { "@type": "Brand", name: "Thermasol Air Systems" },
    description: DESCRIPTION,
    offers: {
      "@type": "Offer",
      url: "https://thermasol-airsystems.fr/sechoir-solaire",
      priceCurrency: "EUR",
      price: "0",
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "EUR",
        price: "0",
        description: "Prix sur devis selon dimensionnement",
      },
      availability: "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "Thermasol Air Systems" },
      areaServed: "Europe",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Thermasol Air Systems",
    url: "https://thermasol-airsystems.fr",
    logo: "https://thermasol-airsystems.fr/logo192.png",
    telephone: "+33 1 84 19 04 49",
    email: "contact@thermasol-airsystems.fr",
  },
];

const ETAPES = [
  [Sun, "Les panneaux PVT air Thermasol 550 (550 Wc / 1 397 Wth) captent le rayonnement solaire."],
  [Thermometer, "Un radiateur aluminium en sous-face transfère la chaleur à l'air aspiré."],
  [Fan, "Un ventilateur pousse l'air chaud vers la chambre de compression puis le produit à sécher."],
  [Zap, "L'électricité produite alimente les ventilateurs, le surplus est autoconsommé ou revendu."],
];

const H2 = ({ children }) => (
  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-14 mb-5">{children}</h2>
);

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left px-5 py-4 font-semibold text-slate-800 hover:bg-slate-50" data-testid={`faq-${q.substring(0, 20)}`}>
        <span>{q}</span>
        <span className="text-orange-500 text-xl ml-4">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="px-5 pb-5 text-slate-600">{a}</p>}
    </div>
  );
}

export default function SechoirSolairePage() {
  return (
    <main data-testid="sechoir-solaire-page" className="bg-white">
      <Seo
        title="Séchoir solaire bois & agricole | Prime CEE | Thermasol Air Systems®"
        description={DESCRIPTION}
        keywords="séchoir solaire, séchoir solaire bois, séchoir solaire agricole, fiche CEE AGRI-EQ-110, AGRI-EQ-110, panneau PVT air, panneau solaire hybride air, panneaux solaires hybrides, séchage solaire par insufflation, produits et co-produits agricoles et forestiers, toiture solaire hybride, système d'insufflation d'air, prime CEE séchage solaire, séchage thermovoltaïque"
        canonical="https://thermasol-airsystems.fr/sechoir-solaire/"
        ogImage="https://thermasol-airsystems.fr/images/seo/hero.webp"
        jsonLd={jsonLd}
      />
      <link
        rel="preload"
        as="image"
        href="/images/seo/hero-640.webp"
        imageSrcSet="/images/seo/hero-640.webp 640w, /images/seo/hero.webp 1024w"
        imageSizes="(max-width: 768px) 100vw, 50vw"
      />

      {/* HERO */}
      <section className="relative pt-28 pb-16 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight">
              Séchoir solaire : séchez votre bois et vos récoltes à l'énergie du soleil
            </h1>
            <p className="text-slate-300 mt-6 leading-relaxed">
              Le séchoir solaire Thermasol Air Systems® transforme la toiture de votre bâtiment en générateur d'air chaud.
              Nos panneaux hybrides (technologie déposée INPI) produisent à la fois de l'électricité et de la chaleur : l'air circule en
              sous-face des panneaux, se réchauffe de 15 à 20 °C et est soufflé dans votre séchoir. Résultat : un séchage
              basse température continu, sans gaz, sans fioul, sans électricité achetée pour le chauffage. Un séchoir
              solaire Thermasol est un système complet neuf éligible à la prime CEE AGRI-EQ-110, qui finance une part
              importante de l'investissement.
            </p>
            <a href="#etude" data-testid="hero-cta-etude" className="inline-block mt-8 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-colors">
              Demander mon étude gratuite
            </a>
          </div>
          <img
            src="/images/seo/hero.webp"
            srcSet="/images/seo/hero-640.webp 640w, /images/seo/hero.webp 1024w"
            sizes="(max-width: 768px) 100vw, 50vw"
            alt="séchoir solaire Thermasol Air Systems sur toiture de hangar bois"
            fetchpriority="high"
            className="rounded-2xl shadow-2xl w-full object-cover"
            width="1024"
            height="687"
          />
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
        <H2>Comment fonctionne un séchoir solaire à panneau PVT air ?</H2>
        <ol className="space-y-4">
          {ETAPES.map(([Ico, txt], i) => (
            <li key={i} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
              <span className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center">{i + 1}</span>
              <Ico className="flex-shrink-0 w-6 h-6 text-orange-500 mt-2" aria-hidden="true" />
              <span className="text-slate-700 pt-1.5">{txt}</span>
            </li>
          ))}
        </ol>
        <p className="text-slate-700 mt-6 leading-relaxed">
          Un <a href="/panneau-pvt-air" className="text-orange-600 font-semibold underline" data-testid="link-panneau-pvt-air">panneau PVT air</a> (photovoltaïque-thermique
          à air) produit de l'électricité sur sa face avant et chauffe directement l'air qui circule en sous-face, sans
          fluide caloporteur, sans glycol, sans échangeur intermédiaire. C'est la seule architecture qui répond à la
          lettre à la fiche AGRI-EQ-110, qui exige un flux d'air en sous-face de chaque panneau.
        </p>
        <p className="text-slate-900 font-semibold mt-4">Soufflage à 25-40 °C : idéal pour le bois, le fourrage et les produits sensibles à la chaleur.</p>

        <H2>Séchoir solaire bois : bûches, plaquettes, sciages</H2>
        <ul className="list-disc pl-6 space-y-2 text-slate-700">
          <li>Bois de chauffage sec (&lt; 20 % d'humidité) en quelques semaines au lieu de 18 à 24 mois.</li>
          <li>Plaquettes forestières et granulés : humidité maîtrisée, meilleur PCI, vente plus chère.</li>
          <li>Scieries : séchage doux des sciages sans fentes ni déformations.</li>
          <li>Référence : <a href="/realisations" className="text-orange-600 font-semibold underline" data-testid="link-realisations">projet de séchoir solaire en Corse</a> — séchoir TERA Structures à Aléria.</li>
        </ul>

        <H2>Séchoir solaire agricole : fourrage, céréales, châtaignes, PPAM</H2>
        <ul className="list-disc pl-6 space-y-2 text-slate-700">
          <li>Séchage en grange du foin et de la luzerne.</li>
          <li>Séchage des céréales, noix, châtaignes, plantes aromatiques.</li>
          <li>Compatible avec les bâtiments agricoles existants (toitures ≥ 500 m²).</li>
        </ul>

        <H2>Prix d'un séchoir solaire et fiche CEE AGRI-EQ-110</H2>
        <p className="text-slate-700 leading-relaxed">
          Le prix d'un séchoir solaire dépend de la surface de toiture, du tonnage à sécher et de l'orientation. Nous
          réalisons gratuitement une étude de dimensionnement (données PVGIS, calcul de la prime selon la fiche CEE
          AGRI-EQ-110).
        </p>
        <p className="text-slate-700 leading-relaxed mt-4">
          La fiche AGRI-EQ-110 « Séchage solaire par insufflation des produits et co-produits agricoles et forestiers
          utilisant des panneaux solaires hybrides » (Certificats d'Économies d'Énergie, secteur Agriculture) finance la
          mise en place d'un système complet de séchage par insufflation d'air, ou d'une toiture solaire en panneaux
          solaires hybrides couplée à un système d'insufflation d'air existant. Le montant de la{" "}
          <a href="/fiche-cee-agri-eq-110" className="text-orange-600 font-semibold underline" data-testid="link-fiche-cee">prime CEE AGRI-EQ-110</a>{" "}
          est calculé en kWh cumac par kW thermique installé, selon la zone climatique (H1, H2, H3) et le type de
          produits (agricoles ou forestiers), sur une durée de vie conventionnelle de 15 ans. Thermasol Air Systems®
          monte le dossier AGRI-EQ-110 pour vous.
        </p>
        <a href="#etude" data-testid="cta-etude-prix" className="inline-block mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-colors">
          Demander mon étude gratuite
        </a>

        <H2>Pourquoi choisir le séchoir solaire Thermasol Air Systems® ?</H2>
        <div className="grid sm:grid-cols-2 gap-5">
          {[
            "Technologie déposée INPI, panneau certifié Solar Keymark.",
            "Panneau PVT air conforme à la fiche AGRI-EQ-110 (flux d'air en sous-face de chaque panneau), sans glycol.",
            "Solution clé en main : étude, fourniture, pose, mise en service, montage du dossier CEE.",
            "Distribution européenne exclusive du panneau Thermasol 550.",
          ].map((txt, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-slate-700 font-medium">{txt}</div>
          ))}
          <a href="/batiment-sechoir-offert" data-testid="card-batiment-offert" className="sm:col-span-2 block bg-orange-500 hover:bg-orange-600 border border-orange-600 rounded-xl p-5 text-white font-bold transition-colors">
            Pas de bâtiment ? On vous l'offre → découvrez le bâtiment séchoir offert
          </a>
        </div>

        <H2>Demandez votre étude gratuite</H2>
        <EtudeForm />

        <H2>Questions fréquentes sur le séchoir solaire</H2>
        <div className="space-y-3">
          {FAQ.map(([q, a]) => <FaqItem key={q} q={q} a={a} />)}
        </div>
      </div>
    </main>
  );
}
